import { useState, useEffect, useRef } from 'react';
import Layout from '@/components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Plus, User, Phone, Mail, Camera, Mic, MicOff, Send, MessageSquare, Calendar, Upload } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CoachingCommunicationPage({ user, onLogout }) {
  const [coaches, setCoaches] = useState([]);
  const [messages, setMessages] = useState([]);
  const [showAddCoachDialog, setShowAddCoachDialog] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const fileInputRef = useRef(null);

  const [newCoach, setNewCoach] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    birthday: '',
    photo_url: ''
  });

  useEffect(() => {
    loadCoaches();
    loadMessages();
  }, []);

  const loadCoaches = async () => {
    try {
      const response = await axios.get(`${API}/coaches?team_id=${user.team_id}`);
      setCoaches(response.data);
    } catch (error) {
      console.error('Failed to load coaches');
    }
  };

  const loadMessages = async () => {
    try {
      const response = await axios.get(`${API}/coaching-messages?team_id=${user.team_id}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to load messages');
    }
  };

  const handleAddCoach = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/coaches`, {
        ...newCoach,
        team_id: user.team_id,
        created_by: user.id
      });
      toast.success('Coach added successfully!');
      setShowAddCoachDialog(false);
      setNewCoach({ first_name: '', last_name: '', phone: '', email: '', birthday: '', photo_url: '' });
      loadCoaches();
    } catch (error) {
      toast.error('Failed to add coach');
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'coach_photo');

    try {
      const response = await axios.post(`${API}/upload-file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setNewCoach({ ...newCoach, photo_url: response.data.file_url });
      toast.success('Photo uploaded!');
    } catch (error) {
      toast.error('Failed to upload photo');
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await sendVoiceMemo(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.info('Recording started...');
    } catch (error) {
      toast.error('Could not access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const sendVoiceMemo = async (audioBlob) => {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'voice-memo.webm');
    formData.append('team_id', user.team_id);
    formData.append('sender_id', user.id);
    formData.append('sender_name', user.name);
    formData.append('message_type', 'voice');

    try {
      await axios.post(`${API}/coaching-messages/voice`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Voice memo sent!');
      loadMessages();
    } catch (error) {
      toast.error('Failed to send voice memo');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      await axios.post(`${API}/coaching-messages`, {
        team_id: user.team_id,
        sender_id: user.id,
        sender_name: user.name,
        content: newMessage,
        message_type: 'text'
      });
      setNewMessage('');
      loadMessages();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-8 space-y-8" data-testid="coaching-communication-page">
        <div className="flex items-center justify-between border-b border-border pb-6">
          <div>
            <h1 className="font-heading text-5xl font-black uppercase tracking-tighter text-primary">
              Coaching Staff
            </h1>
            <p className="text-muted-foreground mt-2">Manage coaches and staff communication</p>
          </div>
          <Dialog open={showAddCoachDialog} onOpenChange={setShowAddCoachDialog}>
            <DialogTrigger asChild>
              <Button data-testid="add-coach-btn">
                <Plus className="h-4 w-4 mr-2" />
                ADD COACH
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl uppercase">Add New Coach</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddCoach} className="space-y-4">
                <div className="flex justify-center">
                  <div className="relative">
                    <div className="w-24 h-24 rounded-full bg-muted border-2 border-primary/30 flex items-center justify-center overflow-hidden">
                      {newCoach.photo_url ? (
                        <img src={`${BACKEND_URL}${newCoach.photo_url}`} alt="Coach" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-12 h-12 text-muted-foreground" />
                      )}
                    </div>
                    <input type="file" ref={fileInputRef} onChange={handlePhotoUpload} accept="image/*" className="hidden" />
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Camera className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>First Name</Label>
                    <Input
                      value={newCoach.first_name}
                      onChange={(e) => setNewCoach({ ...newCoach, first_name: e.target.value })}
                      required
                      data-testid="coach-first-name"
                    />
                  </div>
                  <div>
                    <Label>Last Name</Label>
                    <Input
                      value={newCoach.last_name}
                      onChange={(e) => setNewCoach({ ...newCoach, last_name: e.target.value })}
                      required
                      data-testid="coach-last-name"
                    />
                  </div>
                </div>
                <div>
                  <Label>Phone Number</Label>
                  <Input
                    type="tel"
                    value={newCoach.phone}
                    onChange={(e) => setNewCoach({ ...newCoach, phone: e.target.value })}
                    placeholder="+1 (555) 123-4567"
                    data-testid="coach-phone"
                  />
                </div>
                <div>
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    value={newCoach.email}
                    onChange={(e) => setNewCoach({ ...newCoach, email: e.target.value })}
                    data-testid="coach-email"
                  />
                </div>
                <div>
                  <Label>Birthday</Label>
                  <Input
                    type="date"
                    value={newCoach.birthday}
                    onChange={(e) => setNewCoach({ ...newCoach, birthday: e.target.value })}
                    data-testid="coach-birthday"
                  />
                </div>
                <Button type="submit" className="w-full" data-testid="submit-coach-btn">
                  ADD COACH
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Coaches List */}
          <div className="lg:col-span-1 space-y-4">
            <h2 className="font-heading text-2xl uppercase">Coaching Staff</h2>
            {coaches.length === 0 ? (
              <Card className="border-2">
                <CardContent className="p-6 text-center">
                  <User className="h-12 w-12 mx-auto text-muted-foreground opacity-50" />
                  <p className="text-sm text-muted-foreground mt-2">No coaches added yet</p>
                </CardContent>
              </Card>
            ) : (
              coaches.map((coach) => (
                <Card key={coach.id} className="border-2 hover:border-primary/50 transition-colors" data-testid={`coach-card-${coach.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center overflow-hidden">
                        {coach.photo_url ? (
                          <img src={`${BACKEND_URL}${coach.photo_url}`} alt={coach.first_name} className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-8 h-8 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold">{coach.first_name} {coach.last_name}</h3>
                        {coach.phone && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Phone className="h-3 w-3" /> {coach.phone}
                          </p>
                        )}
                        {coach.email && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Mail className="h-3 w-3" /> {coach.email}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Coaches Chat */}
          <div className="lg:col-span-2">
            <Card className="border-2 h-[600px] flex flex-col">
              <CardHeader className="border-b bg-muted/30 flex-shrink-0">
                <CardTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                  <MessageSquare className="h-6 w-6" />
                  Coaches Chat
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-muted-foreground">No messages yet. Start the conversation!</p>
                  </div>
                ) : (
                  messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%] p-3 rounded-xl ${
                        message.sender_id === user.id
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted'
                      }`}>
                        <p className="text-xs font-semibold mb-1">{message.sender_name}</p>
                        {message.message_type === 'voice' ? (
                          <audio controls src={`${BACKEND_URL}${message.content}`} className="max-w-full" />
                        ) : (
                          <p className="text-sm">{message.content}</p>
                        )}
                        <p className={`text-xs mt-1 ${message.sender_id === user.id ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                          {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
              <div className="p-4 border-t flex-shrink-0">
                <form onSubmit={sendMessage} className="flex gap-2">
                  <Button
                    type="button"
                    variant={isRecording ? 'destructive' : 'outline'}
                    size="icon"
                    onClick={isRecording ? stopRecording : startRecording}
                    data-testid="voice-record-btn"
                  >
                    {isRecording ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                  </Button>
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1"
                    data-testid="coach-message-input"
                  />
                  <Button type="submit" size="icon" data-testid="send-coach-message-btn">
                    <Send className="h-4 w-4" />
                  </Button>
                </form>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}
